import { z } from "zod";

const httpUrl = z
  .string()
  .regex(/^https?:\/\/.+/i, "Must be a valid http(s) URL")
  .max(2000);

// Multi-source product update. Every field is nullable+optional EXCEPT the always-present booleans
// `in_stock`/`active` (bare .optional() — never null, per the house rule). Server-side the controller
// uses null-guarded copy semantics (present-non-null → set; null/omitted → leave unchanged); it does
// not clear a column to NULL. No `source_id` — a product's source is always derived from its brand.
export const updateProductRequestSchema = z.object({
  brand_id: z.number().int().nullable().optional(),
  external_id: z.string().min(1).max(100).nullable().optional(),
  country_code: z.string().regex(/^[A-Z]{2}$/, "Country code must be two uppercase letters (ISO-3166 alpha-2)").nullable().optional(),
  // BCP 47 language tag (e.g. en-CA, fr-CA). Null-guarded server-side like manufacturer — an
  // omitted/null language on a partial update leaves the stored value unchanged. max(35) matches the column.
  // A PRESENT value must be non-blank (\S) — mirrors the create contract + the products.language NOT NULL /
  // non-blank CHECK, so a partial update can't write a whitespace-only language that evades deactivation.
  language: z.string().max(35).regex(/\S/, "Language cannot be blank").nullable().optional(),
  name: z.string().min(1).max(1000).nullable().optional(),
  // Consumer-facing brand, independent of the source-derived brand_name. Null-guarded server-side —
  // an omitted/null manufacturer on a partial update leaves the stored value unchanged.
  manufacturer: z.string().max(200).nullable().optional(),
  category: z.string().max(200).nullable().optional(),
  description: z.string().nullable().optional(),
  price: z.number().min(0).max(9999999999.99).nullable().optional(),
  currency: z.string().regex(/^[A-Z]{3}$/, "Currency must be a three-letter ISO-4217 code").nullable().optional(),
  original_price: z.number().min(0).max(9999999999.99).nullable().optional(),
  url: httpUrl.nullable().optional(),
  tracking_url: httpUrl.nullable().optional(),
  image_url: httpUrl.nullable().optional(),
  rating: z.number().min(0).max(5).nullable().optional(),
  rating_count: z.number().int().min(0).nullable().optional(),
  in_stock: z.boolean().optional(),
  active: z.boolean().optional(),
  specs: z.string().nullable().optional(),
  metadata: z.string().nullable().optional(),
});
